import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AppointmentCountRecurrent, status as AppointmentStatus } from '@spike/appointment-model';
import { AppointmentsStatus, countRecurrentAppointmentsThunk, deleteAppointmentThunk } from '@spike/bookings-action';
import Client from '@spike/client-model';
import { ClientsStatus, getClientThunk } from '@spike/clients-action';
import Invoice from '@spike/invoice-model';
import { getInvoiceThunk, InvoicesStatus, reset as resetInvoices } from '@spike/invoices-action';
import { NewBookingAppointment } from '@spike/new-booking-model';
import { changeBookingStatusThunk, NewBookingsStatus, saveMultipleBookingThunk } from '@spike/new-bookings-action';
import { showSuccess } from '@spike/notifications-action';
import { AppointmentOrderItem, Order, OrderItem, OrderizableTypes, OrderStatus } from '@spike/order-model';
import {
    deleteOrderItemThunk,
    deletePetFromOrderThunk,
    getOrderStatusThunk,
    getOrderThunk,
    migrateBookingThunk,
    OrdersStatus,
    reloadOrderThunk,
    saveNotesThunk,
    verifyOutdatedThunk
} from '@spike/orders-action';
import Pet from '@spike/pet-model';
import { fetchPetsThunk, PetsStatus } from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import clsx from 'clsx';
import { daily } from 'components/Calendar3/CalendarHeader/CalendarSelector/CalendarSelector';
import InvoiceResume from 'components/Checkout2/InvoiceResume';
import PetParent from 'components/Checkout2/PetParent';
import RefreshModal from 'components/Checkout2/UI/RefreshModal';
import { Title } from 'components/Checkout2/UI/Title';
import { getOrderStartTime, groupOrderItemsByPet, sortPets } from 'components/Checkout2/utils/OrderUtils';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import DeclineConfirmation from 'components/Notifications/UI/DeclineConfirmation';
import PetCard from 'components/PetCard';
import { CompleteReport, EditReport, ViewReport } from 'components/Reports';
import { Button, ConfirmDialog, OverDetail, OverFullWindow, SidePanel, Spinner } from 'components/UI';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import { useApiClientWrapper, useAuth } from 'hooks';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import Staff from 'model/Staff';
import { Moment } from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import ClientView from 'pages/Client';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import AddOrderItem from './AddOrderItem';
import AddPet from './AddPet';
import EditOrderItem from './EditOrderItem';
import InvoiceView from './InvoiceView';
import Notes from './Notes';
import OrderDetailHeader from './OrderDetailHeader';
import OrderSubtotalResume from './OrderSubtotalResume';
import PetDetail from './PetDetail';
import RequestedDate from './RequestDate';
import BookingRecurrencyInfo from './UI/BookingRecurrencyInfo';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { fetchServicesThunk } from 'actions/services/ServicesActions';

export interface OrderDetailProps {
    orderId?: number;
    bookingId: number;
    onClose: () => void;
    onCheckout?: (bookingId: number, orderId: number) => void;
    onRebookOrder?: () => void;
    onRebookService?: () => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%'
        },
        horizontalPaddings: {
            paddingLeft: 32,
            paddingRight: 32,
            [theme.breakpoints.down('sm')]: {
                paddingLeft: 16,
                paddingRight: 16
            }
        },
        headerContainer: {
            paddingTop: 37,
            paddingBottom: 18,
            [theme.breakpoints.down('sm')]: {
                paddingTop: 24,
                paddingBottom: 12
            }
        },
        bodyContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%'
        },
        bodyBottomSpace: {
            height: 1,
            border: '1px solid white',
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        footerContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 24,
            paddingBottom: 24,
            borderTop: '1px solid #D4D4D4',
            [theme.breakpoints.down('sm')]: {
                paddingTop: 12,
                paddingBottom: 12
            }
        },
        orderSpinnerContainer: {
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center'
        },
        addButtonSpinner: {
            width: '25px !important',
            height: '25px !important'
        },
        addButtonSpinnerContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 42
        },
        checkoutButton: {
            height: 50,
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                borderRadius: 30
            },
            [theme.breakpoints.up('md')]: {
                height: 55
            }
        },
        section: {
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        petDetail: {
            marginTop: 24
        },

        addAnotherPetButton: {
            'height': 43,
            'width': 'fit-content',
            'padding': '8px 16px !important',
            '& .MuiButton-label': {
                fontSize: 14,
                lineHeight: '27px'
            }
        },
        requestButtonsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
        },
        declineButton: {
            width: '100%',
            marginRight: 15,
            height: 50,
            [theme.breakpoints.up('md')]: {
                height: 55
            }
        },
        acceptButton: {
            width: '100%',
            marginLeft: 15,
            height: 50,
            [theme.breakpoints.up('md')]: {
                height: 55
            }
        },
        viewCalendarButton: {
            'marginTop': 24,
            'height': 43,
            'width': 'fit-content',
            'padding': '8px 16px !important',
            '& .MuiButton-label': {
                fontSize: 14,
                lineHeight: '27px'
            }
        },
        bookingDetailTitle: {
            fontSize: 20
        },
        time: {
            marginTop: 24
        },
        clientOverWindow: {
            zIndex: 99,
            [theme.breakpoints.down('sm')]: {
                left: 0
            },
            [theme.breakpoints.only('md')]: {
                left: '65px'
            },
            [theme.breakpoints.only('lg')]: {
                left: '62px'
            },
            [theme.breakpoints.between(1440, 1920)]: {
                left: '80px'
            },
            [theme.breakpoints.only('xl')]: {
                left: '80px'
            }
        },
        recurrencyContainer: {
            marginTop: 18,
            [theme.breakpoints.up('md')]: {
                marginTop: 24
            }
        },
        lastRecurrentAlertText: {
            fontSize: 16,
            fontHeigh: '18px'
        },
        lastRecurrentWarning: {
            marginTop: 22
        },
        extendButton: {
            marginTop: 10
        },
        invoiceResume: {
            width: '100%',
            borderTop: 'none',
            padding: 0
        }
    })
);

const cancelledStatus = [AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW, AppointmentStatus.DECLINED];

const cleanRelatedEnrollmentFromOrderItem = (orderItem: OrderItem): OrderItem => {
    const clonedOrderItem = cloneDeep(orderItem);

    if (orderItem.redeem === undefined) {
        return clonedOrderItem;
    } else {
        clonedOrderItem.amount = clonedOrderItem.originalPrice ?? '0.00';
        clonedOrderItem.total = undefined;
    }

    return clonedOrderItem;
};

const removeRelatedEnrollments = (order: Order): Order => {
    order.items.appointments.forEach(
        appointmentItem => (appointmentItem.orderItem = cleanRelatedEnrollmentFromOrderItem(appointmentItem.orderItem))
    );
    order.items.addOnServices.forEach(
        addOnServiceItem =>
            (addOnServiceItem.orderItem = cleanRelatedEnrollmentFromOrderItem(addOnServiceItem.orderItem))
    );
    order.items.products.forEach(
        productItem => (productItem.orderItem = cleanRelatedEnrollmentFromOrderItem(productItem.orderItem))
    );

    return order;
};

const cleanUpOrderForRebook = (order: Order): Order => {
    const clonedOrder = cloneDeep(order);

    clonedOrder.items.appointments = clonedOrder?.items.appointments.filter(
        item => item.pet === undefined || (item.pet.active && !item.pet.deceased && !item.pet.deleted)
    );
    clonedOrder.items.addOnServices = clonedOrder?.items.addOnServices.filter(
        item => item.pet === undefined || (item.pet.active && !item.pet.deceased && !item.pet.deleted)
    );
    clonedOrder.items.products = clonedOrder?.items.products.filter(
        item => item.pet === undefined || (item.pet.active && !item.pet.deceased && !item.pet.deleted)
    );
    clonedOrder.items.customs = clonedOrder?.items.customs.filter(
        item => item.pet === undefined || (item.pet.active && !item.pet.deceased && !item.pet.deleted)
    );

    removeRelatedEnrollments(clonedOrder);

    return clonedOrder;
};

const cleanUpServiceForRebook = (appointmentItem: AppointmentOrderItem): AppointmentOrderItem => {
    const clonedappointmentItem = cloneDeep(appointmentItem);
    clonedappointmentItem.orderItem = cleanRelatedEnrollmentFromOrderItem(clonedappointmentItem.orderItem);

    return clonedappointmentItem;
};

export const OrderDetail: FunctionComponent<OrderDetailProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const history = useHistory();
    const auth = useAuth();

    const order = useSelector<RootState, Order | undefined>(state => state.orders.order);
    const orderStatus = useSelector<RootState, OrderStatus | undefined>(state => state.orders.orderStatus);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const orderOutdated = useSelector<RootState, { order: boolean } | undefined>(state => state.orders.outdated);

    const client = useSelector<RootState, Client | undefined>(state => state.clients.client);
    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const recurrentInfo = useSelector<RootState, undefined | AppointmentCountRecurrent>(
        state => state.appointments.recurrent
    );
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);

    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const petsStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);
    const clientPets = useSelector<RootState, Array<Pet>>(state => state.pets.list);

    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const staffs = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const [loading, setLoading] = useState({
        order: true,
        invoice: true,
        pets: true,
        status: false,
        bookingStatus: false,
        recurrent: false,
        extend: false,
        services: false
    });

    const [edit, setEdit] = useState<{ order: boolean; orderItemId: number | undefined }>({
        order: false,
        orderItemId: undefined
    });

    const [add, setAdd] = useState<{ petId: number | undefined; orderItemType: OrderizableTypes | undefined }>({
        petId: undefined,
        orderItemType: undefined
    });

    const [showPetCard, setShowPetCard] = useState<{ petId: number | undefined; optionSelected: string | undefined }>({
        petId: undefined,
        optionSelected: undefined
    });

    const [showClientView, setShowClientView] = useState<{ clientId: number | undefined }>({
        clientId: undefined
    });

    const [showInvoiceView, setShowInvoiceView] = useState<{ invoice: Invoice | undefined }>({
        invoice: undefined
    });

    const [remove, setRemove] = useState<{
        orderItemId: number | undefined;
        type: OrderizableTypes | undefined;
        label: string;
        removing: boolean;
    }>({
        orderItemId: undefined,
        label: '',
        type: undefined,
        removing: false
    });

    const [removePet, setRemovePet] = useState<{
        petId: number | undefined;
        petName: string;
        removing: boolean;
    }>({
        petId: undefined,
        petName: '',
        removing: false
    });

    const [addPet, setAddPet] = useState(false);
    const [showDeclineDialog, setShowDeclineDialog] = useState(false);

    const [notes, setNotes] = useState('');

    const [report, setReport] = useState<{
        appointmentId?: number;
        businessAreaId?: string;
        preshow: 'add' | 'view' | 'none';
        show: 'add' | 'view' | 'edit' | 'none';
        emailConfirmed: boolean;
    }>({ preshow: 'none', show: 'none', emailConfirmed: false });

    const [savingStatusAppointmentId, setSavingStatusAppointmentId] = useState<number | undefined>();

    const [refreshModal, setRefreshModal] = useState({ show: false });

    const [rebook, setRebook] = useState<{
        order: Order | undefined;
        appointmentOrderItem: AppointmentOrderItem | undefined;
    }>({ order: undefined, appointmentOrderItem: undefined });

    const isLastRecurrentAppointment = useMemo(
        () =>
            order && recurrentInfo
                ? order.items.appointments[0].appointment.duration.from.isSame(recurrentInfo.lastDate)
                : false,
        [order, recurrentInfo]
    );

    const existsEditedItemInOrder = useMemo(
        () =>
            [
                ...(order?.items.appointments.map(item => item.orderItem.id) ?? []),
                ...(order?.items.addOnServices.map(item => item.orderItem.id) ?? []),
                ...(order?.items.products.map(item => item.orderItem.id) ?? []),
                ...(order?.items.customs.map(item => item.orderItem.id) ?? [])
            ].includes(edit.orderItemId ?? 0),
        [order, edit.orderItemId]
    );

    const sortedPets = useMemo(() => (order ? sortPets(order) : []), [order]);

    const allPetsInactive = useMemo(
        () => sortedPets.every(pet => !pet.active || pet.deleted || pet.deceased),
        [sortedPets]
    );

    const orderItemsByPet = useMemo(() => (order ? groupOrderItemsByPet(order) : new Map()), [order]);

    const isOrderClosed =
        order?.paid ||
        order?.items.appointments.every(
            item =>
                cancelledStatus.includes(item.appointment.status.id) &&
                (order?.items.addOnServices ?? []).length === 0 &&
                (order?.items.products ?? []).length === 0 &&
                (order?.items.customs ?? []).length === 0
        );

    const isRequestedOrder = (order?.items.appointments ?? []).some(
        item => item.appointment.status.id === AppointmentStatus.REQUESTED
    );

    const isDeclinedOrder = (order?.items.appointments ?? []).some(
        item => item.appointment.status.id === AppointmentStatus.DECLINED
    );

    const headerTitle = isRequestedOrder ? 'Online Booking Request' : edit.order ? 'Edit Booking' : 'Booking Detail';

    const hasOnlyOneService = (order?.items.appointments ?? []).length === 1;
    const hasOnlyOnePet = sortedPets.length === 1;

    const showAddPetButton =
        !loading.pets &&
        clientPets
            .filter(pet => !pet.deleted && pet.active)
            .some(pet => !sortedPets.map(orderPet => orderPet.id).includes(pet.id));

    const loadRecurrentInfo = () => {
        if (order?.items.appointments[0].appointment.recurringUuid) {
            setLoading(prev => ({ ...prev, recurrent: true }));
            dispatch(countRecurrentAppointmentsThunk(apiClientWrapper, order?.items.appointments[0].appointment.id!));
        }
    };

    useEffect(() => {
        if (props.orderId) {
            dispatch(getOrderThunk(apiClientWrapper, props.orderId));
        } else {
            dispatch(migrateBookingThunk(apiClientWrapper, props.bookingId));
        }
        dispatch(resetInvoices());
        dispatch(fetchServicesThunk());
    }, []);

    useEffect(() => {
        switch (serviceStatus) {
            case ServicesStatus.Fetching:
                setLoading(prev => ({ ...prev, services: true }));
                break;
            case ServicesStatus.FetchSuccess:
                setLoading(prev => ({ ...prev, services: false }));
                break;
            case ServicesStatus.Error:
                setLoading(prev => ({ ...prev, services: false }));
                break;
        }
    }, [serviceStatus]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.GetSuccess:
            case OrdersStatus.MigrateSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                order &&
                    setNotes(
                        order.notes && order.notes !== '' ? order.notes : order.items.appointments[0].appointment.notes
                    );
                order?.client.id && dispatch(fetchPetsThunk(apiClientWrapper, order?.client.id));
                if (staffs?.length === 0) {
                    dispatch(fetchStaffThunk());
                }
                if (order?.invoiceId !== undefined) {
                    dispatch(getInvoiceThunk(apiClientWrapper, order?.invoiceId));
                    setLoading(prev => ({ ...prev, invoice: true }));
                } else {
                    setLoading(prev => ({ ...prev, invoice: false }));
                }
                loadRecurrentInfo();

                break;
            case OrdersStatus.ReloadSuccess:
                closeDeleteConfirmationHandler();
                closeEditOrderItemHandler();
                closeDeletePetConfirmationHandler();
                closeReportHandler();
                setLoading(prev => ({ ...prev, order: false, bookingStatus: false }));
                setShowDeclineDialog(false);
                order && setNotes(order.notes);
                if (order?.invoiceId !== undefined) {
                    dispatch(getInvoiceThunk(apiClientWrapper, order?.invoiceId));
                    setLoading(prev => ({ ...prev, invoice: true }));
                } else {
                    setLoading(prev => ({ ...prev, invoice: false }));
                }
                loadRecurrentInfo();
                break;
            case OrdersStatus.DeleteOrderItemSuccess:
            case OrdersStatus.DeletePetFromOrderSuccess:
                closeDeleteConfirmationHandler();
                closeEditOrderItemHandler();
                closeDeletePetConfirmationHandler();
                closeReportHandler();
                break;
            case OrdersStatus.NotFound:
                setRefreshModal(prev => ({ ...prev, show: true }));
                closeDeleteConfirmationHandler();
                closeDeletePetConfirmationHandler();
                break;
            case OrdersStatus.GetStatusSuccess:
                setLoading(prev => ({ ...prev, status: false }));
                if (order?.paid === orderStatus?.paid) {
                    order && props.onCheckout && props.onCheckout(props.bookingId, order?.id);
                } else {
                    setRefreshModal(prev => ({ ...prev, show: true }));
                }
                break;
            case OrdersStatus.VerifyOutdatedSuccess:
                if (['view', 'add'].includes(report.preshow)) {
                    if (orderOutdated?.order) {
                        setReport(prev => ({
                            ...prev,
                            prehow: 'none',
                            show: 'none',
                            appointmentId: undefined,
                            businessAreaId: undefined,
                            emailConfirmed: false
                        }));
                        setRefreshModal({ show: true });
                    } else {
                        setReport(prev => ({ ...prev, preshow: 'none', show: prev.preshow }));
                    }
                }
                break;
            case OrdersStatus.Error:
                setLoading(prev => ({ ...prev, order: false }));
                setRemove(prev => ({ ...prev, removing: false }));
                setRemovePet(prev => ({ ...prev, removing: false }));
                break;
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.NotFound:
                setRefreshModal(prev => ({ ...prev, show: true }));
                closeDeleteConfirmationHandler();
                closeDeletePetConfirmationHandler();
                break;
            case NewBookingsStatus.ChangeStatusSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case NewBookingsStatus.SaveMultipleSuccess:
                //dispatch(reloadOrderThunk(apiClientWrapper));
                //setLoading(prev => ({ ...prev, extend: false }));
                dispatch(showSuccess('The recurring series is being extended. No need to extend it again.'));
                setTimeout(() => loadRecurrentInfo(), 20000);
                break;
            case NewBookingsStatus.Error:
                setLoading(prev => ({ ...prev, bookingStatus: false, extended: false }));
        }
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (appointmentsStatus) {
            case AppointmentsStatus.CountRecurrentSuccess:
                setLoading(prev => ({ ...prev, recurrent: false, extend: false }));
                break;
            case AppointmentsStatus.DeleteSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                break;
            case AppointmentsStatus.NotFound:
                setRemove(prev => ({ ...prev, removing: false, orderItemId: undefined, type: undefined }));
                setRefreshModal(prev => ({ ...prev, show: true }));
                break;
            case AppointmentsStatus.Error:
                setRemove(prev => ({ ...prev, removing: false }));
                setLoading(prev => ({ ...prev, recurrent: false }));
                break;
        }
    }, [appointmentsStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.GetInvoiceSuccess:
                setLoading(prev => ({ ...prev, invoice: false }));
                break;
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, invoice: false }));
                break;
        }
    }, [invoicesStatus]);

    useNonInitialEffect(() => {
        switch (petsStatus) {
            case PetsStatus.FetchSuccess:
                setLoading(prev => ({ ...prev, pets: false }));
                break;
            case PetsStatus.Error:
                setLoading(prev => ({ ...prev, pets: false }));
                break;
        }
    }, [petsStatus]);

    useNonInitialEffect(() => {
        switch (clientsStatus) {
            case ClientsStatus.GetSuccess:
                setLoading(prev => ({ ...prev, emailConfirmed: client ? confirmEmail(client) : false }));
                break;
            case ClientsStatus.Error:
                setReport(prev => ({ ...prev, emailConfirmed: false }));
                break;
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            order && order.notes !== notes && dispatch(saveNotesThunk(apiClientWrapper, order.id, notes));
        }, 600);
        return () => clearTimeout(timer);
    }, [notes]);

    const editOrderHandler = () => {
        setEdit(prev => ({ ...prev, order: true }));
    };

    const closeEditOrderHandler = () => {
        setEdit(prev => ({ ...prev, order: false }));
    };

    const editOrderItemHandler = (orderItemId: number) => {
        setEdit(prev => ({ ...prev, order: false, orderItemId }));
    };

    const closeEditOrderItemHandler = () => {
        setEdit(prev => ({ ...prev, orderItemId: undefined }));
    };

    const addOrderItemHandler = (petId: number, orderItemType: OrderizableTypes) => {
        setAdd(prev => ({ ...prev, petId, orderItemType }));
    };

    const closeAddOrderItemHandler = () => {
        setAdd(prev => ({ ...prev, petId: undefined, orderItemType: undefined }));
    };

    const confirmDeleteHanlder = (orderItemId: number) => {
        let label = '';
        let type: OrderizableTypes | undefined = undefined;

        if (order?.items.appointments.some(item => item.orderItem.id === orderItemId)) {
            label = 'Service';
            type = OrderizableTypes.APPOINTMENT;
        } else if (order?.items.addOnServices.some(item => item.orderItem.id === orderItemId)) {
            label = 'Add-On';
            type = OrderizableTypes.ADDON_SERVICE;
        } else if (order?.items.products.some(item => item.orderItem.id === orderItemId)) {
            label = 'Product';
            type = OrderizableTypes.PRODUCT;
        } else if (order?.items.customs.some(item => item.orderItem.id === orderItemId)) {
            label = 'Custom Item';
            type = OrderizableTypes.CUSTOM;
        }
        setRemove(prev => ({ ...prev, orderItemId, label, type }));
    };

    const deleteHanlder = () => {
        setRemove(prev => ({ ...prev, removing: true }));

        if (remove.type === OrderizableTypes.APPOINTMENT) {
            const appointmentId = order?.items.appointments.find(item => item.orderItem.id === remove.orderItemId)
                ?.appointment.id;
            appointmentId && dispatch(deleteAppointmentThunk(apiClientWrapper, appointmentId));
        } else {
            order &&
                remove.orderItemId &&
                dispatch(deleteOrderItemThunk(apiClientWrapper, order.id, remove.orderItemId));
        }
    };

    const closeDeleteConfirmationHandler = () => {
        setRemove(prev => ({ ...prev, orderItemId: undefined, label: '', type: undefined, removing: false }));
    };

    const addReportHandler = (appointmentId: number, businessAreaId: string) => {
        order && setReportClientEmail(appointmentId, order);
        setReport(prev => ({ ...prev, preshow: 'add', show: 'none', appointmentId, businessAreaId }));
        order?.id && dispatch(verifyOutdatedThunk(apiClientWrapper, order.id, order.version));
    };

    const viewReportHandler = (appointmentId: number, businessAreaId: string) => {
        order && setReportClientEmail(appointmentId, order);
        setReport(prev => ({ ...prev, preshow: 'view', show: 'none', appointmentId, businessAreaId }));
        order?.id && dispatch(verifyOutdatedThunk(apiClientWrapper, order.id, order.version));
    };

    const editReportHandler = () => {
        setReport(prev => ({ ...prev, show: 'edit' }));
    };

    const closeReportHandler = () => {
        setReport(prev => ({
            ...prev,
            show: 'none',
            appointmentId: undefined,
            businessAreaId: undefined,
            emailConfirmed: false
        }));
    };

    const addedReportHandler = () => {
        dispatch(reloadOrderThunk(apiClientWrapper));
    };

    const confirmEmail = (client: Client) => {
        return !client.temporalEmail && !isEmpty(client.email);
    };

    const setReportClientEmail = (appointmentId: number, order: Order) => {
        const appointmentClientId = order.items.appointments.find(item => item.appointment.id === appointmentId)
            ?.appointment.customer.id;

        if (appointmentClientId === order.client.id) {
            setReport(prev => ({ ...prev, emailConfirmed: confirmEmail(order.client) }));
        } else if (client && client.id === appointmentClientId) {
            setReport(prev => ({ ...prev, emailConfirmed: confirmEmail(client) }));
        } else {
            appointmentClientId && dispatch(getClientThunk(apiClientWrapper, appointmentClientId));
        }
    };

    const confirmDeletePetHandler = (pet: Pet) => {
        setRemovePet(prev => ({ ...prev, petId: pet.id, petName: pet.name }));
    };

    const deletePetHanlder = () => {
        setRemovePet(prev => ({ ...prev, removing: true }));
        order?.id && removePet.petId && dispatch(deletePetFromOrderThunk(apiClientWrapper, order.id, removePet.petId));
    };

    const closeDeletePetConfirmationHandler = () => {
        setRemovePet(prev => ({ ...prev, petId: undefined, petName: '', removing: false }));
    };

    const closePetCard = () => setShowPetCard(prev => ({ ...prev, petId: undefined, optionSelected: undefined }));
    const closeClientView = () => setShowClientView(prev => ({ ...prev, clientId: undefined }));

    const refreshHandler = () => {
        setShowPetCard(prev => ({ ...prev, petId: undefined, optionSelected: undefined }));
        closeClientView();
        closeEditOrderHandler();
        closeEditOrderItemHandler();
        closeDeleteConfirmationHandler();
        closeDeletePetConfirmationHandler();
        setRefreshModal(prev => ({ ...prev, show: false }));
        setLoading(prev => ({ ...prev, order: true, invoice: true }));
        dispatch(reloadOrderThunk(apiClientWrapper));
        dispatch(fetchPetsThunk(apiClientWrapper, order?.client.id ?? 0));
    };

    const checkOrderStatusHandler = () => {
        if (order) {
            setLoading(prev => ({ ...prev, status: true }));
            dispatch(getOrderStatusThunk(apiClientWrapper, order?.id));
        }
    };

    const declineHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowDeclineDialog(true);
    };

    const confirmDeclineHandler = (sendNotification: boolean) => {
        setLoading(prev => ({ ...prev, bookingStatus: true }));
        changeBookingStatus(AppointmentStatus.DECLINED, sendNotification);
    };

    const acceptHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setLoading(prev => ({ ...prev, bookingStatus: true }));
        changeBookingStatus(AppointmentStatus.BOOKED);
    };

    const changeBookingStatus = async (status: number, sendNotification = false) => {
        await dispatch(changeBookingStatusThunk(apiClientWrapper, props.bookingId, status, false, sendNotification));
    };

    const goToCalendaryDayHandler = (order: Order) => {
        props.onClose();

        const start = getOrderStartTime(order);

        start !== null && history.push(`/calendar?calendar=${daily.id}&date=${start.format('YYYY-MM-DD')}`);
    };

    const closeRebookHandler = () => {
        setRebook({
            order: undefined,
            appointmentOrderItem: undefined
        });
    };

    const rebookedHandler = () => {
        if (rebook.order !== undefined && props.onRebookOrder) {
            props.onRebookOrder();
        } else if (rebook.appointmentOrderItem !== undefined && props.onRebookService) {
            props.onRebookService();
        } else {
            props.onClose();
        }
    };

    const setOrderToRebook = () => {
        if (order) {
            const orderToRebooked = cleanUpOrderForRebook(order);
            setRebook({
                order: orderToRebooked,
                appointmentOrderItem: undefined
            });
        }
    };

    const setServiceToRebook = (appointmentItem: AppointmentOrderItem) => {
        setRebook({
            order: undefined,
            appointmentOrderItem: cleanUpServiceForRebook(appointmentItem)
        });
    };

    const showInvoiceHandler = () => {
        if (order?.paid && invoice) {
            setShowInvoiceView({ invoice });
        }
    };

    const extend = () => {
        if (order) {
            setLoading(prev => ({ ...prev, extend: true }));

            const appointment = order?.items.appointments[0].appointment;
            const orderItem = order?.items.appointments[0].orderItem;

            let startDate: Moment | null = appointment.duration.from.clone();

            switch (appointment.recurringFrecuency?.id) {
                case 'every_day':
                    startDate = startDate.add(1, 'day');
                    break;
                case 'every_week':
                    startDate = startDate.add(1, 'week');
                    break;
                case 'every_two_weeks':
                    startDate = startDate.add(2, 'week');
                    break;
                case 'every_three_weeks':
                    startDate = startDate.add(3, 'week');
                    break;
                case 'every_four_weeks':
                    startDate = startDate.add(4, 'week');
                    break;
                case 'every_month':
                    startDate = startDate.add(1, 'month');
                    break;
                case 'every_five_weeks':
                    startDate = startDate.add(5, 'week');
                    break;
                case 'every_six_weeks':
                    startDate = startDate.add(6, 'week');
                    break;
                case 'every_eight_weeks':
                    startDate = startDate.add(8, 'week');
                    break;
                case 'annually':
                    startDate = startDate.add(1, 'year');
                    break;
                default:
                    startDate = null;
            }

            if (startDate && auth.user) {
                const durationInMs = appointment.duration.to
                    .clone()
                    .diff(appointment.duration.from.clone(), 'milliseconds');
                const endDate = startDate.clone().add(durationInMs, 'milliseconds');

                const newAppointment: NewBookingAppointment = {
                    petId: appointment.pet.id,
                    serviceId: appointment.services[0].id,
                    date: startDate,
                    notes: appointment.notes,
                    createdByStaffId: appointment.createdBy ?? auth.user.staffId,
                    clientId: appointment.customer.id,
                    staffId: appointment.services[0].staff?.id,
                    dateEnd: endDate,
                    orderItemPrice: Number(orderItem.amount),
                    discount: orderItem.discount
                };

                dispatch(
                    saveMultipleBookingThunk(
                        apiClientWrapper,
                        [newAppointment],
                        undefined,
                        appointment.recurringFrecuency?.id,
                        appointment.recurringUuid,
                        false
                    )
                );
            }
        }
    };

    return (
        <>
            {!rebook.order && !rebook.appointmentOrderItem && !showPetCard.petId && !showClientView.clientId && (
                <SidePanel
                    side="right"
                    showCloseButton={true}
                    onClose={props.onClose}
                >
                    {(edit.orderItemId === undefined || !existsEditedItemInOrder) &&
                        add.orderItemType === undefined &&
                        !addPet && (
                            <Box className={classes.container}>
                                <Box className={clsx(classes.headerContainer, classes.horizontalPaddings)}>
                                    <OrderDetailHeader
                                        title={loading.order ? '' : headerTitle}
                                        loading={
                                            loading.order || loading.invoice || savingStatusAppointmentId !== undefined
                                        }
                                        withoutOptions={isRequestedOrder || isDeclinedOrder}
                                        allPetsInactive={allPetsInactive}
                                        editing={edit.order}
                                        paid={order?.paid}
                                        orderClosed={isOrderClosed}
                                        onlyOnePet={sortedPets.length === 1}
                                        onClose={props.onClose}
                                        onBack={edit.order ? closeEditOrderHandler : undefined}
                                        onEdit={editOrderHandler}
                                        onRebook={setOrderToRebook}
                                        onShowInvoice={showInvoiceHandler}
                                        onShowLog={() => {}}
                                        onDelete={() => {}}
                                    />
                                </Box>
                                <OverlayScrollbarsComponent style={{ height: '100%' }}>
                                    <Box className={clsx(classes.bodyContainer, classes.horizontalPaddings)}>
                                        {(loading.order || loading.services) && (
                                            <Box className={classes.orderSpinnerContainer}>
                                                <Spinner />
                                            </Box>
                                        )}
                                        {!loading.order && order && isRequestedOrder && (
                                            <RequestedDate date={order.createdAt} />
                                        )}
                                        {!loading.order && order && (
                                            <PetParent
                                                client={order.client}
                                                onShowClient={() =>
                                                    setShowClientView(prev => ({ ...prev, clientId: order.client.id }))
                                                }
                                            />
                                        )}
                                        {!loading.order && order && isRequestedOrder && (
                                            <Title
                                                title="Booking Detail"
                                                className={clsx(classes.section, classes.bookingDetailTitle)}
                                            />
                                        )}
                                        {!loading.order && order && isRequestedOrder && (
                                            <Button
                                                color="black"
                                                label="View on Calendar"
                                                onClick={() => goToCalendaryDayHandler(order)}
                                                className={classes.viewCalendarButton}
                                            />
                                        )}
                                        {!loading.order &&
                                            order &&
                                            order?.items.appointments[0] &&
                                            order.items.appointments[0].appointment.recurringUuid &&
                                            !loading.recurrent && (
                                                <Box className={classes.recurrencyContainer}>
                                                    <BookingRecurrencyInfo
                                                        recurrent={recurrentInfo}
                                                        duration={order?.items.appointments[0].appointment.duration}
                                                    />
                                                </Box>
                                            )}
                                        {!loading.order &&
                                            !loading.recurrent &&
                                            order &&
                                            isLastRecurrentAppointment && (
                                                <AlertNewBooking
                                                    bWidth="2px"
                                                    icon={faInfoCircle}
                                                    iconSize="lg"
                                                    iconColor="#BAA997"
                                                    bgColor="#F8F5F1"
                                                    bdColor="#BCB8AE"
                                                    className={classes.lastRecurrentWarning}
                                                >
                                                    <Typography className={classes.lastRecurrentAlertText}>
                                                        Only the recurring service for one pet will be extended. Any
                                                        added services or pets won’t be included.
                                                    </Typography>
                                                    <Button
                                                        label="Extend"
                                                        color="black"
                                                        onClick={extend}
                                                        className={classes.extendButton}
                                                        loading={loading.extend}
                                                    />
                                                </AlertNewBooking>
                                            )}
                                        {!loading.order &&
                                            order &&
                                            sortedPets.map(pet => (
                                                <PetDetail
                                                    key={pet.id}
                                                    pet={pet}
                                                    orderItems={orderItemsByPet.get(pet.id)}
                                                    onlyOnePet={hasOnlyOnePet}
                                                    editingOrder={edit.order}
                                                    editable={!isOrderClosed}
                                                    withoutOptions={isRequestedOrder || isDeclinedOrder}
                                                    disableDeleteService={hasOnlyOneService}
                                                    savingAppointmentId={savingStatusAppointmentId}
                                                    className={classes.petDetail}
                                                    onEdit={editOrderItemHandler}
                                                    onAdd={orderItemType => addOrderItemHandler(pet.id!, orderItemType)}
                                                    onDelete={confirmDeleteHanlder}
                                                    onViewReport={viewReportHandler}
                                                    onAddReport={addReportHandler}
                                                    onDeletePet={() => confirmDeletePetHandler(pet)}
                                                    onStartSavingStatus={appointmentId =>
                                                        setSavingStatusAppointmentId(appointmentId)
                                                    }
                                                    onStopSavingStatus={appointmentId =>
                                                        setSavingStatusAppointmentId(undefined)
                                                    }
                                                    onRebook={setServiceToRebook}
                                                    onShowPetCard={(optSelected?: string) =>
                                                        setShowPetCard(prev => ({
                                                            ...prev,
                                                            petId: pet.id,
                                                            optionSelected: optSelected
                                                        }))
                                                    }
                                                />
                                            ))}
                                        {!loading.order && loading.pets && (
                                            <Box className={clsx(classes.addButtonSpinnerContainer, classes.section)}>
                                                <Spinner classNameCircularProgress={classes.addButtonSpinner} />
                                            </Box>
                                        )}
                                        {!loading.order &&
                                            !loading.pets &&
                                            !isOrderClosed &&
                                            showAddPetButton &&
                                            !isRequestedOrder && (
                                                <Button
                                                    color="green"
                                                    label="+ Add Another Pet"
                                                    size="small"
                                                    className={clsx(classes.addAnotherPetButton, classes.section)}
                                                    disabled={savingStatusAppointmentId !== undefined}
                                                    onClick={() => setAddPet(true)}
                                                />
                                            )}
                                        {!loading.order && (
                                            <Notes
                                                notes={notes}
                                                className={classes.section}
                                                onChange={setNotes}
                                            />
                                        )}
                                        {!loading.order && order && <Box className={classes.bodyBottomSpace} />}
                                    </Box>
                                </OverlayScrollbarsComponent>
                                <Box className={clsx(classes.footerContainer, classes.horizontalPaddings)}>
                                    {order?.paid ? (
                                        <InvoiceResume
                                            invoice={invoice}
                                            loadingInvoice={loading.invoice}
                                            loadingTip={loading.invoice}
                                            className={classes.invoiceResume}
                                        />
                                    ) : (
                                        <OrderSubtotalResume
                                            order={order}
                                            invoice={invoice}
                                            loadingOrder={loading.order}
                                            loadingInvoice={loading.invoice}
                                            cancelledStatus={cancelledStatus}
                                        />
                                    )}
                                    {!edit.order &&
                                        !order?.paid &&
                                        !isOrderClosed &&
                                        !loading.order &&
                                        !loading.invoice &&
                                        !isRequestedOrder && (
                                            <Button
                                                label="Check Out"
                                                color="black"
                                                size="large"
                                                disabled={remove.removing || savingStatusAppointmentId !== undefined}
                                                loading={loading.status}
                                                className={classes.checkoutButton}
                                                onClick={checkOrderStatusHandler}
                                            />
                                        )}
                                    {isRequestedOrder && (
                                        <Box className={classes.requestButtonsContainer}>
                                            <Button
                                                label="Decline"
                                                size="large"
                                                variant="danger"
                                                className={classes.declineButton}
                                                onClick={declineHandler}
                                                loading={loading.bookingStatus}
                                            />
                                            <Button
                                                label="Accept"
                                                size="large"
                                                color="green"
                                                className={classes.acceptButton}
                                                onClick={acceptHandler}
                                                loading={loading.bookingStatus}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    {edit.orderItemId && order && existsEditedItemInOrder && (
                        <EditOrderItem
                            order={order}
                            orderItemId={edit.orderItemId}
                            recurrentInfo={recurrentInfo}
                            hideRemove={hasOnlyOneService}
                            onClose={closeEditOrderItemHandler}
                            onCloseOrderDetail={props.onClose}
                            onDelete={() => confirmDeleteHanlder(edit.orderItemId!)}
                            onRebook={(appointmentItem: AppointmentOrderItem) => {
                                setEdit(prev => ({ ...prev, orderItemId: undefined }));
                                setRebook({
                                    order: undefined,
                                    appointmentOrderItem: appointmentItem
                                });
                            }}
                        />
                    )}
                    {add.orderItemType && add.petId && order && (
                        <AddOrderItem
                            order={order}
                            bookingId={props.bookingId}
                            petId={add.petId}
                            type={add.orderItemType}
                            onClose={closeAddOrderItemHandler}
                        />
                    )}
                    {addPet && order && (
                        <AddPet
                            order={order}
                            bookingId={props.bookingId}
                            onClose={() => setAddPet(false)}
                        />
                    )}
                    {report.show === 'view' && report.appointmentId && report.businessAreaId && (
                        <OverDetail
                            fullWindow
                            fullHeight
                        >
                            <ViewReport
                                appointmentId={report.appointmentId}
                                businessAreaId={report.businessAreaId}
                                onBack={closeReportHandler}
                                onClose={closeReportHandler}
                                onEditGrooming={editReportHandler}
                                onEditDental={editReportHandler}
                            />
                        </OverDetail>
                    )}
                    {report.show === 'edit' && report.appointmentId && report.businessAreaId && (
                        <OverDetail
                            fullWindow
                            fullHeight
                        >
                            <EditReport
                                appointmentId={report.appointmentId!}
                                businessAreaId={report?.businessAreaId!}
                                savePDF={true}
                                onBack={closeReportHandler}
                                onClose={closeReportHandler}
                                onView={() => setReport(prev => ({ ...prev, show: 'view' }))}
                                declined={false}
                                emailConfigured={report.emailConfirmed}
                            />
                        </OverDetail>
                    )}
                    {report.show === 'add' && report.appointmentId && report.businessAreaId && (
                        <OverDetail
                            fullWindow
                            fullHeight
                        >
                            <CompleteReport
                                appointmentId={report.appointmentId}
                                businessAreaId={report.businessAreaId}
                                onBack={closeReportHandler}
                                onClose={closeReportHandler}
                                declined={false}
                                onlyAddReport={true}
                                emailConfigured={report.emailConfirmed}
                                onSave={addedReportHandler}
                            />
                        </OverDetail>
                    )}
                    {showInvoiceView.invoice && (
                        <OverDetail
                            fullWindow
                            fullHeight
                        >
                            <InvoiceView
                                invoice={showInvoiceView.invoice}
                                onBack={() => setShowInvoiceView(prev => ({ ...prev, invoice: undefined }))}
                            />
                        </OverDetail>
                    )}
                </SidePanel>
            )}
            {remove.orderItemId && (
                <ConfirmDialog
                    open={true}
                    title={`Delete ${remove.label}`}
                    question={
                        <Typography>
                            Are you sure you want to <strong>delete</strong> this {remove.label}?
                        </Typography>
                    }
                    cancelButtonLabel="No"
                    confirmButtonLabel="Yes"
                    processing={remove.removing}
                    onCancel={closeDeleteConfirmationHandler}
                    onConfirm={deleteHanlder}
                />
            )}
            {removePet.petId && (
                <ConfirmDialog
                    open={true}
                    title={`Delete Pet`}
                    question={
                        <Typography>
                            Are you sure you want to <strong>delete</strong> this for{' '}
                            <strong>{removePet.petName}</strong>?
                        </Typography>
                    }
                    cancelButtonLabel="No"
                    confirmButtonLabel="Yes"
                    processing={removePet.removing}
                    onCancel={closeDeletePetConfirmationHandler}
                    onConfirm={deletePetHanlder}
                />
            )}
            {refreshModal.show && (
                <RefreshModal
                    onRefresh={refreshHandler}
                    onClose={() => setRefreshModal(prev => ({ ...prev, show: false }))}
                />
            )}
            {showDeclineDialog && (
                <DeclineConfirmation
                    loading={loading.bookingStatus}
                    onCancel={() => setShowDeclineDialog(false)}
                    onConfirm={sendNotification => confirmDeclineHandler(sendNotification)}
                />
            )}
            {(rebook.order || rebook.appointmentOrderItem) && (
                <CreateBookingDrawer
                    parentID={order?.client.id}
                    petID={
                        rebook?.order?.items?.appointments[0]?.appointment?.pet?.id ??
                        rebook?.appointmentOrderItem?.appointment?.pet?.id
                    }
                    order={rebook.order}
                    appointmentItem={rebook.appointmentOrderItem}
                    onClose={closeRebookHandler}
                    onBooked={rebookedHandler}
                />
            )}
            {showPetCard.petId && (
                <PetCard
                    petId={showPetCard.petId}
                    optionMenuSelected={showPetCard.optionSelected}
                    onSaved={refreshHandler}
                    onClose={refreshHandler}
                />
            )}
            {showClientView.clientId && (
                <OverFullWindow
                    className={classes.clientOverWindow}
                    onClose={closeClientView}
                >
                    <ClientView
                        clientId={showClientView.clientId}
                        hideHeader={true}
                        onClose={closeClientView}
                        onClientSaved={refreshHandler}
                    />
                </OverFullWindow>
            )}
        </>
    );
};

export default OrderDetail;
